import { memo, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { trpc } from 'root/src/client/v2/trpc/trpc';
import { NotificationState } from 'root/src/shared/notification/notification.types';
import { useRedesignModal } from 'root/src/client/v2/common/contexts/redesignModal.context';
import { RedesignModalLayout } from 'root/src/client/v2/common/components/RedesignModalLayout';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { NOTIFICATION_MARKDOWN_COMPONENTS } from 'root/src/client/v2/notifications/redesign/notificationMarkdown';
// The shared redesign notification modal card: walks a queue of notifications one at a time
// (Next / Close) inside a `RedesignModalLayout`. Used by the forced popup (`RedesignNotifications`)
// and by clicking a row in the navbar bell (a single notification).
export const NotificationModalContent = memo(({ notifications, closeable = false, markReadOnAdvance = true }) => {
    const { hide } = useRedesignModal();
    const utils = trpc.useUtils();
    const readMutation = trpc.notification.read.useMutation();
    const [index, setIndex] = useState(0);
    const notification = notifications[index];
    const hasNext = index < notifications.length - 1;
    const handleAdvance = () => {
        if (markReadOnAdvance && (notification === null || notification === void 0 ? void 0 : notification.state) === NotificationState.Unread) {
            // The server throws if already read; ignore and let the bell's poll/invalidate reconcile.
            readMutation.mutateAsync({ notificationId: notification.id })
                .then(() => utils.notification.list.invalidate())
                .catch(() => undefined);
        }
        if (hasNext) {
            setIndex(value => value + 1);
            return;
        }
        hide();
    };
    if (!notification) {
        return null;
    }
    return (<RedesignModalLayout className="sm:max-w-xl" onClose={closeable ? hide : undefined}>
			<div className="flex w-full flex-col gap-5">
				<div className="flex items-center gap-3 pr-8">
					<span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-neon-cyan/10 text-neon-cyan">
						<NotificationsNoneIcon sx={{ fontSize: 18 }}/>
					</span>
					<h2 className="font-display text-lg font-semibold text-foreground">Notifications</h2>
					{notifications.length > 1 && (<span className="ml-auto text-xs font-medium text-muted-foreground">
							{index + 1} / {notifications.length}
						</span>)}
				</div>
				<div className="max-h-[50vh] overflow-y-auto break-words text-sm leading-relaxed text-foreground/90">
					<Markdown remarkPlugins={[remarkGfm]} components={NOTIFICATION_MARKDOWN_COMPONENTS}>
						{notification.content}
					</Markdown>
				</div>
				<Button variant="brand" className="w-full" onClick={handleAdvance}>
					{hasNext ? 'Next' : 'Close'}
				</Button>
			</div>
		</RedesignModalLayout>);
});
NotificationModalContent.displayName = 'NotificationModalContent';
